import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaUser, FaEnvelope, FaLock, FaUserShield, FaCheck, FaBuilding } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { isValidEmail, isStrongPassword } from '../../utils/validators';
import api from '../../utils/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

/**
 * Admin Register component for system administrator signup
 * Requires an admin registration code for security (except for the first admin)
 */
const AdminRegister = () => {
  const { register, isAuthenticated, user } = useAuth();
  const { showToast, showError } = useToast();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    orgName: '',
    adminCode: '',
    terms: false,
    role: 'system_admin' // Fixed role for admin registration
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isFirstAdmin, setIsFirstAdmin] = useState(false);
  const [checkingAdminStatus, setCheckingAdminStatus] = useState(true);
  
  // Check if this is the first admin
  useEffect(() => {
    const checkFirstAdmin = async () => {
      try {
        const res = await api.get('/auth/check-first-admin');
        setIsFirstAdmin(res.data.isFirstAdmin);
      } catch (err) {
        console.error('Error checking first admin status:', err);
        // Default to requiring a code if we can't verify
        setIsFirstAdmin(false);
      } finally {
        setCheckingAdminStatus(false);
      }
    };
    
    checkFirstAdmin();
  }, []);
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'system_admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/');
      }
    }
  }, [isAuthenticated, user, navigate]);
  
  /**
   * Handle form input changes
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  /**
   * Validate form inputs
   */
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!isStrongPassword(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters and include a number, uppercase letter, and special character';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.orgName.trim()) {
      newErrors.orgName = 'Organization name is required';
    }
    
    // Only require admin code if not the first admin
    if (!isFirstAdmin && !formData.adminCode.trim()) {
      newErrors.adminCode = 'Admin registration code is required';
    }
    
    if (!formData.terms) {
      newErrors.terms = 'You must agree to the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        orgName: formData.orgName,
        adminCode: formData.adminCode || undefined,
        phoneNumber: formData.phoneNumber || formData.email // Use email as phone if not provided
      };
      
      const result = await register(userData);
      
      if (result.success) {
        showToast('Registration successful! Welcome to Meira Administration.', 'success');
        navigate('/admin/dashboard');
      } else {
        // Handle registration error
        if (result.details && result.details.length > 0) {
          // Format backend validation errors
          const backendErrors = {};
          result.details.forEach(err => {
            backendErrors[err.param] = err.msg;
          });
          setErrors(backendErrors);
        } else {
          setErrors({
            general: result.error || 'Registration failed. Please try again.'
          });
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({
        general: 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Password strength indicators
  const getPasswordStrength = (password) => {
    if (!password) return 'none';
    
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const isLongEnough = password.length >= 8;
    
    const criteria = [hasUppercase, hasLowercase, hasNumbers, hasSpecialChar, isLongEnough];
    const strength = criteria.filter(Boolean).length;
    
    if (strength <= 2) return 'weak';
    if (strength <= 4) return 'medium';
    return 'strong';
  };
  
  const passwordStrength = getPasswordStrength(formData.password);
  
  if (checkingAdminStatus) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <Helmet>
        <title>Admin Registration | Meira</title>
      </Helmet>
      
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Create Admin Account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Or{' '}
          <Link to="/login" className="font-medium text-primary hover:text-primary-dark">
            sign in to your existing account
          </Link>
        </p>
      </div>
      
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {isFirstAdmin && (
            <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-md">
              <p className="text-sm">
                <FaUserShield className="inline-block mr-1" />
                You are creating the first system administrator account. No admin code is required.
              </p>
            </div>
          )}
          
          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
              <p className="text-sm">{errors.general}</p>
            </div>
          )}
          
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="firstName"
                    id="firstName"
                    className={`block w-full pl-10 sm:text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary ${
                      errors.firstName ? 'border-red-300' : ''
                    }`}
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </div>
                {errors.firstName && (
                  <p className="mt-2 text-sm text-red-600">{errors.firstName}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="lastName"
                    id="lastName"
                    className={`block w-full pl-10 sm:text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary ${
                      errors.lastName ? 'border-red-300' : ''
                    }`}
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </div>
                {errors.lastName && (
                  <p className="mt-2 text-sm text-red-600">{errors.lastName}</p>
                )}
              </div>
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaEnvelope className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  name="email"
                  id="email"
                  className={`block w-full pl-10 sm:text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary ${
                    errors.email ? 'border-red-300' : ''
                  }`}
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              {errors.email && (
                <p className="mt-2 text-sm text-red-600">{errors.email}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="orgName" className="block text-sm font-medium text-gray-700">
                Organization Name
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaBuilding className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="orgName"
                  id="orgName"
                  className={`block w-full pl-10 sm:text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary ${
                    errors.orgName ? 'border-red-300' : ''
                  }`}
                  placeholder="Your organization"
                  value={formData.orgName}
                  onChange={handleChange}
                />
              </div>
              {errors.orgName && (
                <p className="mt-2 text-sm text-red-600">{errors.orgName}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  name="password"
                  id="password"
                  className={`block w-full pl-10 sm:text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary ${
                    errors.password ? 'border-red-300' : ''
                  }`}
                  placeholder="********"
                  value={formData.password}
                  onChange={handleChange}
                />
              </div>
              
              {/* Password strength indicator */}
              {formData.password && (
                <div className="mt-2">
                  <div className="flex items-center">
                    <span className="text-xs mr-2">Password strength:</span>
                    <div className="flex-grow h-2 rounded-full bg-gray-200 overflow-hidden">
                      <div
                        className={`h-full ${
                          passwordStrength === 'weak'
                            ? 'bg-red-500 w-1/3'
                            : passwordStrength === 'medium'
                            ? 'bg-yellow-500 w-2/3'
                            : 'bg-green-500 w-full'
                        }`}
                      ></div>
                    </div>
                    <span
                      className={`ml-2 text-xs ${
                        passwordStrength === 'weak'
                          ? 'text-red-500'
                          : passwordStrength === 'medium'
                          ? 'text-yellow-500'
                          : 'text-green-500'
                      }`}
                    >
                      {passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}
                    </span>
                  </div>
                </div>
              )}
              
              {errors.password && (
                <p className="mt-2 text-sm text-red-600">{errors.password}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  name="confirmPassword"
                  id="confirmPassword"
                  className={`block w-full pl-10 sm:text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary ${
                    errors.confirmPassword ? 'border-red-300' : ''
                  }`}
                  placeholder="********"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
              </div>
              {errors.confirmPassword && (
                <p className="mt-2 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>
            
            {!isFirstAdmin && (
              <div>
                <label htmlFor="adminCode" className="block text-sm font-medium text-gray-700">
                  Admin Registration Code
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUserShield className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    name="adminCode"
                    id="adminCode"
                    className={`block w-full pl-10 sm:text-sm border-gray-300 rounded-md focus:ring-primary focus:border-primary ${
                      errors.adminCode ? 'border-red-300' : ''
                    }`}
                    placeholder="Enter admin code"
                    value={formData.adminCode}
                    onChange={handleChange}
                  />
                </div>
                {errors.adminCode && (
                  <p className="mt-2 text-sm text-red-600">{errors.adminCode}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Contact an admin for the registration code.
                </p>
              </div>
            )}
            
            <div className="flex items-center">
              <input
                id="terms"
                name="terms"
                type="checkbox"
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                checked={formData.terms}
                onChange={handleChange}
              />
              <label htmlFor="terms" className="ml-2 block text-sm text-gray-900">
                I agree to the{' '}
                <Link to="/terms" className="font-medium text-primary hover:text-primary-dark">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link
                  to="/privacy"
                  className="font-medium text-primary hover:text-primary-dark"
                >
                  Privacy Policy
                </Link>
              </label>
            </div>
            {errors.terms && (
              <p className="mt-2 text-sm text-red-600">{errors.terms}</p>
            )}
            
            <div>
              <button
                type="submit"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center">
                    <LoadingSpinner size="sm" color="white" className="mr-2" />
                    Creating Account...
                  </span>
                ) : (
                  'Create Admin Account'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminRegister; 